import { Component, ElementRef, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { InfocenterProtokoll, TxInfocenterUser } from '../../shared/interfaces/protokollInfocenter';
import { TxApiService } from '../../shared/services/txapi.service';
import { InfocenterService } from '../../shared/services/infocenter.service';
import { AuthService } from '../../shared/services/auth.service';
import { IS_TEAMLEADER } from '../../store/entities/Permissions';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

/** What the list is split into. Independent of the sort order, unlike the previous version. */
export type InfocenterGroupMode = 'none' | 'department' | 'group' | 'team' | 'status';

/** What the people inside a group are sorted by. */
export type InfocenterSortMode = 'name' | 'status' | 'lastBooking';

export interface InfocenterTeam {
	id: number;
	name: string;
}

export interface InfocenterGroup {
	/** Raw value the group was built from, '' for the single group of an ungrouped list. */
	key: string;
	label: string;
	/** Status labels come from the translation file, every other label is data. */
	translateLabel: boolean;
	users: TxInfocenterUser[];
}

@Component({
	selector: '[infocenter]',
	templateUrl: 'infocenter.template.html',
})
export class InfoCenterComponent implements OnInit, OnDestroy {

	constructor(
		private el: ElementRef,
		private myTxApiserice: TxApiService,
		private myInfocenterService: InfocenterService,
		private myAuthService: AuthService,
		private translate: TranslateService
	) {
		this.infoCenterSubscription = this.myInfocenterService.getInfoCenter().subscribe((myInfocenterData) => {
			this.myInfocenter = myInfocenterData;
			this.rebuild();
		});
	}

	/**
	 * Order of the status groups, from present to absent. Alphabetical would open the list
	 * with "Abwesend", and who is there is the first thing anyone opens this panel for.
	 * Matched against the raw status the webservice sends, so the order holds in every
	 * language. Anything unknown lands after these, ahead of the unassigned bucket.
	 */
	private static readonly STATUS_ORDER = ['Anwesend', 'Pause', 'Dienstgang', 'Abwesend'];
	@Output() toggleChatEvent: EventEmitter<any> = new EventEmitter();

	myInfocenter: InfocenterProtokoll;

	searchTerm = '';
	groupMode: InfocenterGroupMode = 'none';
	sortMode: InfocenterSortMode = 'name';
	sortAscending = true;
	selectedTeamIds: number[] = [];
	onlyMyEmployees = false;

	/** The grouping/sorting/filter block eats a lot of panel height, so it folds away. */
	settingsOpen = localStorage.getItem('txInfocenterSettingsOpen') === '1';

	/** Result of the current search, filter, sort and grouping - what the template renders. */
	groups: InfocenterGroup[] = [];
	availableTeams: InfocenterTeam[] = [];
	totalCount = 0;
	visibleCount = 0;

	/**
	 * An empty order column would still claim its share of the row and cut the names short -
	 * which is the very complaint this rework started from. Most of the time nobody has a
	 * running order, so the column only appears when there is something to put in it.
	 */
	showOrderColumn = false;

	/**
	 * The team fields and the teamleader flag are additions to getInfocenter that may not be
	 * deployed yet. Both controls are hidden while the data is missing rather than showing a
	 * filter that silently matches nobody.
	 */
	hasTeamData = false;
	hasAssignmentData = false;

	private infoCenterSubscription: Subscription;

	// Sorts the way the user's language does, so umlauts land next to their base letter instead
	// of after Z - with lists of German staff names that is the difference between "Müller"
	// sitting under M or at the very bottom.
	private readonly collator = new Intl.Collator(undefined, { sensitivity: 'base', numeric: true });

	ngOnInit(): void {
		document.querySelector('tx-layout')?.classList.add('chat-sidebar-container');
	}

	ngOnDestroy(): void {
		this.infoCenterSubscription?.unsubscribe();
	}

	toggleInfoCenter(): void {
		this.toggleChatEvent.emit(null);
	}

	toggleSettings(): void {
		this.settingsOpen = !this.settingsOpen;
		localStorage.setItem('txInfocenterSettingsOpen', this.settingsOpen ? '1' : '0');
	}

	/**
	 * The team filter chips only mean something to a teamleader — everyone else has never
	 * seen those team names and would only be confused by them.
	 */
	get isTeamleader(): boolean {
		return this.myAuthService.can(IS_TEAMLEADER);
	}

	onSearchChange(term: string): void {
		this.searchTerm = term;
		this.rebuild();
	}

	clearSearch(): void {
		this.onSearchChange('');
	}

	setGroupMode(mode: InfocenterGroupMode): void {
		this.groupMode = mode;
		this.rebuild();
	}

	/** Picking the active sort again flips the direction, picking another one starts ascending. */
	setSortMode(mode: InfocenterSortMode): void {
		if (this.sortMode === mode) {
			this.sortAscending = !this.sortAscending;
		} else {
			this.sortMode = mode;
			this.sortAscending = true;
		}
		this.rebuild();
	}

	toggleSortDirection(): void {
		this.sortAscending = !this.sortAscending;
		this.rebuild();
	}

	toggleTeam(teamId: number): void {
		if (this.selectedTeamIds.includes(teamId)) {
			this.selectedTeamIds = this.selectedTeamIds.filter((id) => id !== teamId);
		} else {
			this.selectedTeamIds = [...this.selectedTeamIds, teamId];
		}
		this.rebuild();
	}

	isTeamSelected(teamId: number): boolean {
		return this.selectedTeamIds.includes(teamId);
	}

	clearTeams(): void {
		this.selectedTeamIds = [];
		this.rebuild();
	}

	toggleOnlyMyEmployees(): void {
		this.onlyMyEmployees = !this.onlyMyEmployees;
		this.rebuild();
	}

	hasActiveFilters(): boolean {
		return !!this.searchTerm || this.selectedTeamIds.length > 0 || this.onlyMyEmployees;
	}

	resetFilters(): void {
		this.searchTerm = '';
		this.selectedTeamIds = [];
		this.onlyMyEmployees = false;
		this.rebuild();
	}

	trackByGroup(index: number, group: InfocenterGroup): string {
		return group.key;
	}

	trackByUser(index: number, user: TxInfocenterUser): number {
		return user.Personalnummer;
	}

	/** The coloured dot: status decides the colour, an active order overrides it with blue. */
	statusIconClass(user: TxInfocenterUser): string {
		return `text-${user.Status}-${user.AuftragAktiv}`;
	}

	/**
	 * Text of the status column. The webservice sends a free-text state for some people
	 * ("Homeoffice"); everyone else falls back to the translated presence status. Translated
	 * rather than raw, so a French user searching for "présent" finds what their screen says.
	 * Used by the template and by the search, so the two cannot drift apart.
	 */
	displayStatus(user: TxInfocenterUser): string {
		if (user.state_text) {
			return user.state_text;
		}

		return user.Status ? this.translate.instant('infocenter.' + user.Status) : '';
	}

	/** Order and work step of a running order, joined for the single "order" column. */
	orderText(user: TxInfocenterUser): string {
		return [user.AuftragsBezeichnung, user.AuftragsschrittBezeichnung].filter((part) => !!part).join(' · ');
	}

	private rebuild(): void {
		const all = this.myInfocenter?.Infocenters ?? [];

		this.totalCount = all.length;
		this.hasTeamData = all.some((user) => !!user.TeamId);
		this.hasAssignmentData = all.some((user) => user.IsAssignedToMe !== null && user.IsAssignedToMe !== undefined);
		this.availableTeams = this.collectTeams(all);

		// A team that disappeared between two refreshes would otherwise keep filtering everything
		// away, with no chip left to switch it off again.
		const knownTeamIds = this.availableTeams.map((team) => team.id);
		this.selectedTeamIds = this.selectedTeamIds.filter((id) => knownTeamIds.includes(id));

		if (!this.hasAssignmentData) {
			this.onlyMyEmployees = false;
		}
		if (!this.hasTeamData && this.groupMode === 'team') {
			this.groupMode = 'none';
		}

		const matching = all.filter((user) => this.matchesFilters(user));
		const sorted = [...matching].sort((a, b) => this.compareUsers(a, b));

		this.visibleCount = sorted.length;
		this.showOrderColumn = sorted.some((user) => !!this.orderText(user));
		this.groups = this.buildGroups(sorted);
	}

	private collectTeams(users: TxInfocenterUser[]): InfocenterTeam[] {
		const byId = new Map<number, string>();

		users.forEach((user) => {
			if (user.TeamId) {
				byId.set(user.TeamId, user.TeamName || String(user.TeamId));
			}
		});

		return Array.from(byId, ([id, name]) => ({ id, name })).sort((a, b) => this.collator.compare(a.name, b.name));
	}

	private matchesFilters(user: TxInfocenterUser): boolean {
		if (this.onlyMyEmployees && user.IsAssignedToMe !== 1) {
			return false;
		}

		if (this.selectedTeamIds.length && !this.selectedTeamIds.includes(user.TeamId)) {
			return false;
		}

		return this.matchesSearch(user);
	}

	/**
	 * Every entered word has to appear somewhere, not the whole phrase in one field - typing
	 * "mei support" finds Meier in the support department. Same rule as the other search fields
	 * in the portal.
	 */
	private matchesSearch(user: TxInfocenterUser): boolean {
		const terms = this.searchTerm
			.toLowerCase()
			.split(' ')
			.map((term) => term.trim())
			.filter((term) => !!term);

		if (!terms.length) {
			return true;
		}

		const haystack = [
			user.Name,
			user.Abteilung,
			user.Gruppe,
			user.TeamName,
			// Whatever the status column actually shows has to be searchable, otherwise
			// "support anwesend" finds nobody while the screen is full of them.
			this.displayStatus(user),
		]
			.filter((part) => !!part)
			.join(' ')
			.toLowerCase();

		return terms.every((term) => haystack.includes(term));
	}

	private compareUsers(a: TxInfocenterUser, b: TxInfocenterUser): number {
		let result = 0;

		if (this.sortMode === 'status') {
			result = this.collator.compare(a.Status ?? '', b.Status ?? '');
		} else if (this.sortMode === 'lastBooking') {
			result = this.compareBookingTime(a, b);
		}

		// Name is the tie breaker for every mode, so people with the same status keep a stable,
		// readable order instead of whatever the webservice happened to send.
		if (result === 0) {
			result = this.collator.compare(a.Name ?? '', b.Name ?? '');
		}

		return this.sortAscending ? result : -result;
	}

	private compareBookingTime(a: TxInfocenterUser, b: TxInfocenterUser): number {
		const left = a.LetzteBuchungsZeit || '';
		const right = b.LetzteBuchungsZeit || '';

		if (!left && !right) {
			return 0;
		}

		// People without a booking stay at the bottom in both directions. The values are ISO
		// timestamps, so an empty one would otherwise sort first and fill the top of the list
		// with blank rows the moment the direction is flipped.
		if (!left) {
			return this.sortAscending ? 1 : -1;
		}
		if (!right) {
			return this.sortAscending ? -1 : 1;
		}

		return left < right ? -1 : left > right ? 1 : 0;
	}

	private buildGroups(users: TxInfocenterUser[]): InfocenterGroup[] {
		if (this.groupMode === 'none') {
			return users.length ? [{ key: '', label: '', translateLabel: false, users }] : [];
		}

		const buckets = new Map<string, TxInfocenterUser[]>();

		users.forEach((user) => {
			const key = this.groupKeyOf(user);
			const bucket = buckets.get(key);

			if (bucket) {
				bucket.push(user);
			} else {
				buckets.set(key, [user]);
			}
		});

		const groups: InfocenterGroup[] = Array.from(buckets, ([key, groupUsers]) => ({
			key,
			label: key,
			translateLabel: this.groupMode === 'status',
			users: groupUsers,
		}));

		// Departments and groups come back as null for some people. The old version turned that
		// into a group literally headed "null"; it is now one labelled bucket that always sits
		// last, whichever way the list is sorted.
		return groups.sort((a, b) => {
			if (!a.key !== !b.key) {
				return a.key ? -1 : 1;
			}

			if (this.groupMode === 'status') {
				const rank = this.statusRank(a.key) - this.statusRank(b.key);

				if (rank !== 0) {
					return rank;
				}
			}

			return this.collator.compare(a.label, b.label);
		});
	}

	private statusRank(status: string): number {
		const index = InfoCenterComponent.STATUS_ORDER.indexOf(status);

		return index === -1 ? InfoCenterComponent.STATUS_ORDER.length : index;
	}

	private groupKeyOf(user: TxInfocenterUser): string {
		switch (this.groupMode) {
			case 'department':
				return user.Abteilung || '';
			case 'group':
				return user.Gruppe || '';
			case 'team':
				return user.TeamName || '';
			case 'status':
				return user.Status || '';
			default:
				return '';
		}
	}
}
